'use client'

import { useState, useEffect, Suspense } from 'react'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client'
import { isComingSoonMode, getTrialDurationDays } from '@/lib/config'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Logo } from '@/components/Logo'
import { AlertCircle, CheckCircle, Globe, Sparkles, ArrowRight, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { locales, type Locale, getLocale, setLocale } from '@/i18n/client'
import GetUpdates from '@/components/GetUpdates'

const languageNames: Record<Locale, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  ja: '日本語',
  ko: '한국어',
  zh: '中文',
  ru: 'Русский',
  ar: 'العربية',
  hi: 'हिन्दी',
  nl: 'Nederlands',
  pl: 'Polski',
  tr: 'Türkçe',
}

function SignupContent() {
  const t = useTranslations()
  const searchParams = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [currentLocale, setCurrentLocale] = useState<Locale>('en')
  const supabase = createClient()

  // Get URL params for redirect and plan selection
  const redirectUrl = searchParams.get('redirect')
  const planId = searchParams.get('plan')
  const isTrialFlow = searchParams.get('trial') === 'true'
  const trialDays = getTrialDurationDays()

  useEffect(() => {
    setCurrentLocale(getLocale())
  }, [])

  const handleLanguageChange = (locale: Locale) => {
    setLocale(locale)
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess(false)

    if (password !== confirmPassword) {
      setError(t('auth.errors.passwordMismatch'))
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError(t('auth.errors.weakPassword'))
      setLoading(false)
      return
    }

    try {
      // Determine redirect destination after email confirmation
      let destination = '/app/dashboard'

      if (redirectUrl) {
        // Use the provided redirect URL
        destination = decodeURIComponent(redirectUrl)
      } else if (planId || isTrialFlow) {
        // Redirect to checkout if coming from pricing page
        const checkoutParams = new URLSearchParams()
        if (planId) checkoutParams.set('plan', planId)
        if (isTrialFlow) checkoutParams.set('trial', 'true')
        destination = `/checkout?${checkoutParams.toString()}`
      }

      // Build callback URL with redirect destination
      const callbackUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(destination)}`

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: callbackUrl,
        },
      })

      if (error) throw error

      setSuccess(true)

      // Don't initialize language preferences here - no session exists yet
      // User will be redirected after email confirmation to the appropriate page
      // (checkout with plan/trial, or dashboard)
    } catch (err: any) {
      setError(err.message || t('auth.errors.signupFailed'))
    } finally {
      setLoading(false)
    }
  }

  // Check if in Coming Soon mode
  const comingSoon = isComingSoonMode()

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md relative z-10">
        {/* Language Selector */}
        <div className="flex justify-end mb-6">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2 rounded-full">
                <Globe className="h-4 w-4" />
                {languageNames[currentLocale]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[400px] overflow-y-auto">
              {locales.map((locale) => (
                <DropdownMenuItem
                  key={locale}
                  onClick={() => handleLanguageChange(locale)}
                  className={currentLocale === locale ? 'bg-muted text-foreground' : ''}
                >
                  {languageNames[locale]}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Logo and Tagline */}
        <div className="text-center mb-10">
          <Link href="/" className="inline-flex justify-center mb-4">
            <Logo size="lg" />
          </Link>
          <p className="text-muted-foreground">
            {comingSoon ? t('comingSoon.description') : t('auth.signupTagline')}
          </p>

          {/* Trial banner */}
          {isTrialFlow && !comingSoon && (
            <div className="mt-6 p-4 bg-speaker-3/10 rounded-xl">
              <div className="flex items-center justify-center gap-2 text-speaker-3-text">
                <Sparkles className="w-5 h-5" />
                <span className="font-semibold">{t('auth.trialBanner', { days: trialDays })}</span>
              </div>
            </div>
          )}
        </div>

        {comingSoon ? (
          <GetUpdates />
        ) : (
          <Card className="rounded-2xl shadow-float">
            <CardHeader className="text-center pb-6">
              <div className="inline-flex items-center justify-center gap-2 mx-auto mb-4 px-4 py-1.5 rounded-full bg-speaker-2/10">
                <Sparkles className="h-3.5 w-3.5 text-speaker-2" />
                <span className="text-xs font-semibold text-speaker-2 tracking-wide">
                  Get started
                </span>
              </div>
              <CardTitle className="text-2xl font-semibold">{t('auth.signup')}</CardTitle>
              <CardDescription className="text-muted-foreground">{t('auth.signupDescription')}</CardDescription>
            </CardHeader>

            <form onSubmit={handleSignup}>
              <CardContent className="space-y-5">
                {error && (
                  <div className="flex items-center gap-3 p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-xl text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="flex items-center gap-3 p-4 bg-success/10 text-success rounded-xl text-sm">
                    <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{t('auth.signupSuccess')}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading || success}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium">{t('auth.password')}</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading || success}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium">{t('auth.confirmPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading || success}
                    className="h-12"
                  />
                </div>
              </CardContent>

              <CardFooter className="flex flex-col gap-5">
                <Button
                  type="submit"
                  className="w-full group"
                  size="lg"
                  disabled={loading || success}
                >
                  {loading ? t('common.loading') : t('auth.signup')}
                  {!loading && !success && <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </Button>

                <div className="text-sm text-center text-muted-foreground">
                  {t('auth.hasAccount')}{' '}
                  <Link href="/login" className="text-foreground hover:text-speaker-2 font-semibold transition-colors">
                    {t('auth.login')}
                  </Link>
                </div>
              </CardFooter>
            </form>
          </Card>
        )}

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-flex items-center gap-1">
            ← {t('common.backToHome', { defaultValue: 'Back to home' })}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default function SignupPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-accent border-t-transparent" />
      </div>
    }>
      <SignupContent />
    </Suspense>
  )
}
